import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Accept': 'application/json',
  },
});

// Interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const auth = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
};

export const profile = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
};

export const services = {
  getAll: () => api.get('/services'),
  filter: (params) => api.get('/services/filter', { params }),
  export: (params) => api.get('/services/export', { params, responseType: 'blob' }),
  delete: (id) => api.delete(`/services/${id}`),
};

export const bikes = {
  getAll: () => api.get('/bikes'),
  create: (data) => api.post('/bikes', data),
  update: (id, data) => api.put(`/bikes/${id}`, data),
  delete: (id) => api.delete(`/bikes/${id}`),
  getTemplate: () => api.get('/bikes/template', { responseType: 'blob' }),
  import: (formData) => api.post('/bikes/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const spareParts = {
  getAll: (params = {}) => api.get('/spare-parts', { params }),
  create: (data) => api.post('/spare-parts', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => {
    if (data instanceof FormData) data.append('_method', 'PUT');
    return api.post(`/spare-parts/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  delete: (id) => api.delete(`/spare-parts/${id}`),
  getTemplate: () => api.get('/spare-parts/template', { responseType: 'blob' }),
  import: (formData) => api.post('/spare-parts/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  importImages: (formData) => api.post('/spare-parts/import-images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const masterSpareParts = {
  getAll: (params = {}) => api.get('/master-spare-parts', { params }),
  search: (partNumber) => api.get('/master-spare-parts/search', { params: { part_number: partNumber } }),
  create: (data) => api.post('/master-spare-parts', data),
  update: (id, data) => api.patch(`/master-spare-parts/${id}`, data),
  delete: (id) => api.delete(`/master-spare-parts/${id}`),
  getTemplate: () => api.get('/master-spare-parts/template', { responseType: 'blob' }),
  import: (formData) => api.post('/master-spare-parts/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const bikeParts = {
  getAll: () => api.get('/bike-parts'),
  getByBike: (bikeId) => api.get(`/bike-parts/bike/${bikeId}`),
  create: (bikeId, data) => api.post(`/bike-parts/bike/${bikeId}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => {
    if (data instanceof FormData) data.append('_method', 'PUT');
    return api.post(`/bike-parts/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  delete: (id) => api.delete(`/bike-parts/${id}`),
  getTemplate: () => api.get('/bike-parts/template', { responseType: 'blob' }),
  import: (formData) => api.post('/bike-parts/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  importImages: (formData) => api.post('/bike-parts/import-images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const stations = {
  getAll: () => api.get('/stations'),
  create: (data) => api.post('/stations', data),
  update: (id, data) => api.put(`/stations/${id}`, data),
  delete: (id) => api.delete(`/stations/${id}`),
  getTemplate: () => api.get('/stations/template', { responseType: 'blob' }),
  import: (formData) => api.post('/stations/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const customers = {
  getAll: (params = {}) => api.get('/customers', { params }),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  getTemplate: () => api.get('/customers/template', { responseType: 'blob' }),
  import: (formData) => api.post('/customers/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const stationContacts = {
  getAll: (stationId) => api.get(`/stations/${stationId}/contacts`),
  create: (stationId, data) => api.post(`/stations/${stationId}/contacts`, data),
  update: (stationId, id, data) => api.put(`/stations/${stationId}/contacts/${id}`, data),
  delete: (stationId, id) => api.delete(`/stations/${stationId}/contacts/${id}`),
};

export const users = {
  getMechanicsList: () => api.get('/users/mechanics-list'),
};

export const stocks = {
  getAll: (params = {}) => api.get('/stocks', { params }),
  update: (data) => api.post('/stocks', data),
  import: (formData) => api.post('/stocks/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export default api;
